// Org-level view: "<Org> — all projects" in the switcher combines an org's live projects
// (e.g. a website and its iOS app). Overview and Traffic aggregate across them; Performance
// and Conversions stay per project, since vitals and goals are defined per project.
import type { ByProject, ProjectInfo } from "../src/types";

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const PLATFORM: Record<string, string> = { web: "Web", ios: "iOS", android: "Android" };

export const isOrg = (p: ProjectInfo | undefined) => p?.kind === "org";

/** Switcher options: projects first, then the org-level entries in their own group. */
export function projectOptions(list: ProjectInfo[], current: string): string {
  const opt = (p: ProjectInfo) => `<option value="${esc(p.id)}"${p.id === current ? " selected" : ""}>${esc(p.name)}</option>`;
  const orgs = list.filter(isOrg);
  const singles = list.filter((p) => !isOrg(p));
  return singles.map(opt).join("") + (orgs.length ? `<optgroup label="Whole organisation">${orgs.map(opt).join("")}</optgroup>` : "");
}

/** Under the Overview heading: visitor ids differ per project, so one person on web and app counts twice. */
export function orgNote(p: ProjectInfo): string {
  if (!isOrg(p)) return "";
  const names = (p.projects ?? []).map((x) => esc(x.name)).join(", ");
  return `<div class="notice">Combined view of ${names}. Visitors are counted per project, so someone who used both is counted once in each.</div>`;
}

/** Performance and Conversions are per project: say so, and offer each project. */
export function orgOnlyNotice(p: ProjectInfo, screen: "performance" | "conversions" | "errors" | "realtime"): string {
  const what = screen === "performance" ? "Web Vitals are" : screen === "errors" ? "Errors are" : screen === "realtime" ? "Realtime is" : "Goals and funnels are";
  const links = (p.projects ?? []).map((x) => `<button class="link" data-org-pick="${esc(x.id)}">${esc(x.name)}</button>`).join(" · ");
  return `<div class="notice">${what} set up per project, so they aren't combined for the whole organisation. Pick a single project: ${links}</div>`;
}

/** Fills `el` with the "By project" card: visitors and sessions per project and platform. */
export async function orgSplit(el: HTMLElement | null, params: Record<string, string>, stale: () => boolean): Promise<void> {
  if (!el) return;
  const res = await fetch(`/api/by_project?${new URLSearchParams(params)}`, { credentials: "same-origin" });
  if (stale()) return;
  if (!res.ok) {
    el.innerHTML = `<div class="empty">Couldn't load the split by project.</div>`;
    return;
  }
  const { rows } = (await res.json()) as ByProject;
  if (stale()) return;
  if (!rows.length) {
    el.innerHTML = `<div class="empty">No data for this range yet.</div>`;
    return;
  }
  const grid = "grid-template-columns:minmax(0,1fr) 80px 96px 96px";
  const max = Math.max(1, ...rows.map((r) => r.visitors));
  el.innerHTML = `<div class="rows"><div class="rhead" style="${grid}"><span>Project</span><span>Platform</span><span>Visitors</span><span>Sessions</span></div>${rows
    .map(
      (r) => `<div class="row" style="${grid}"><div class="barcell"><div class="bar" style="width:${((r.visitors / max) * 100).toFixed(1)}%"></div><span class="lbl">${esc(r.name)}</span></div>
      <span class="n">${esc(PLATFORM[r.platform] ?? (r.platform || "–"))}</span><span class="n p">${fmtN(r.visitors)}</span><span class="n">${fmtN(r.sessions)}</span></div>`,
    )
    .join("")}</div>`;
}

/** The card shell for `orgSplit`, placed at the top of Traffic. */
export const orgSplitCard = (p: ProjectInfo) =>
  isOrg(p)
    ? `<section class="card"><div class="card-head"><h2>By project</h2><span class="spacer"></span></div><div id="org-split"><div class="skeleton" style="height:120px"></div></div></section>`
    : "";

/** "Pick a single project" links in the notices switch the project. */
export function onOrgPick(switchProject: (id: string) => void): void {
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-org-pick]");
    if (!b) return;
    const sel = document.getElementById("project") as HTMLSelectElement | null;
    if (sel) sel.value = b.dataset.orgPick!;
    switchProject(b.dataset.orgPick!);
  });
}
